import React from 'react';
import { Box } from '@mui/material';
import { useFormikContext } from 'formik';
import {
  useAIProviders,
  useAIModels,
  useAIModelExecutionParameters,
} from 'hooks/PromptFunction';
import FieldSkeleton from 'sharedComponents/Skeleton/FieldSkeleton';
import {
  SparkDropdown,
  DynamicUIComponentFieldController,
} from 'sharedComponents/SparkForm';
import { SparkAccordion } from 'sharedComponents/UI';
import { SliderValues, SliderOptions } from 'types/common';
import { DropdownOption } from 'types/common/DropdownOption';
import type { PromptFunctionsFormValueType } from 'types/PromptFunctions';
import { parseJSONCleaned } from 'utilities/index';

type Props = {
  onProviderChange: (providerId: string | number) => void;
  onChangeAiModel: (aiModelId: string | number) => void;
  isParentLoading?: boolean;
};

function PromptFunctionConfigSection({
  onProviderChange,
  onChangeAiModel,
  isParentLoading,
}: Props) {
  const { values, setValues } =
    useFormikContext<PromptFunctionsFormValueType>();
  const {
    joinedAiModelParameter,
    isLoading: isAiModelParameterLoading,
    isFetching,
  } = useAIModelExecutionParameters(values.aiModel);

  const {
    dropdownOptions: providerOptions,
    isLoading: isAiProviderOptionsLoading,
  } = useAIProviders();

  const {
    dropdownOptions: aiModelsOptions,
    isLoading: isAiModalOptionsLoading,
  } = useAIModels(values.provider);

  const handleProviderChange = (providerId: string | number) => {
    onProviderChange(providerId);
    setValues((prev) => {
      return {
        ...prev,
        aiModel: '',
      };
    });
  };

  const isLoading =
    isAiModelParameterLoading ||
    isFetching ||
    isParentLoading ||
    isAiModalOptionsLoading ||
    isAiProviderOptionsLoading;

  return (
    <SparkAccordion
      title='Config'
      accordionTitleProps={{
        variant: 'body1',
        color: 'rgba(0,0,0,0.6)',
        fontFamily: 'Mont-SemiBold',
      }}
      summaryStyles={{
        cursor: 'pointer',
        paddingTop: 0,
      }}
    >
      {isAiModalOptionsLoading || isAiProviderOptionsLoading ? (
        <Box display='flex' flexDirection='column' gap={1}>
          <FieldSkeleton sx={{ margin: 0 }} />
        </Box>
      ) : (
        <>
          <SparkDropdown
            name='provider'
            children={providerOptions}
            label='Provider'
            size={12}
            onChange={handleProviderChange}
            disabled={isAiProviderOptionsLoading}
            imgStyles={{ aspectRatio: '1' }}
          />
          <SparkDropdown
            name='aiModel'
            children={aiModelsOptions}
            label='AI Model'
            size={12}
            onChange={onChangeAiModel}
            disabled={isAiModalOptionsLoading}
          />
        </>
      )}
      {isLoading ? (
        <Box display='flex' flexDirection='column' gap={1}>
          <FieldSkeleton sx={{ margin: 0 }} />
        </Box>
      ) : (
        <>
          {joinedAiModelParameter?.map((aiParameter) => {
            const { parameterAttributes } = aiParameter;
            const {
              enumValues,
              placeholderText,
              uiComponentID,
              subHeaderText,
              headerText,
              helpText,
              sliderValues,
              key,
            } = parameterAttributes || {};

            const optionEnumValues = parseJSONCleaned<any>(enumValues || '');
            const parsedSliderValues = parseJSONCleaned<SliderValues>(
              sliderValues || ''
            );

            const sliderOptions = parsedSliderValues?.sliderOptions;

            const _sliderOptions: SliderOptions = {
              min: Number(sliderOptions?.min),
              max: Number(sliderOptions?.max),
              decimalPrecision: Number(sliderOptions?.decimalPrecision),
              step: Number(sliderOptions?.stepSize),
              renderPercentage: !!sliderOptions?.renderPercentage,
            };

            const options: DropdownOption[] =
              optionEnumValues?.options?.map((option: any) => ({
                value: option.value,
                label: option.title,
                iconUrl: option.iconURL,
                description: option.description,
              })) || [];

            return (
              <DynamicUIComponentFieldController
                name={key}
                helpText={helpText}
                label={headerText}
                placeholder={placeholderText}
                subHeaderText={subHeaderText}
                uiComponentID={uiComponentID}
                sliderOptions={_sliderOptions}
                key={`${values.aiModel}-${aiParameter.id}-${aiParameter.aiModelExecutionParameterID}`}
                options={options}
              />
            );
          })}
        </>
      )}
    </SparkAccordion>
  );
}

export default PromptFunctionConfigSection;
